import { GQL } from '@aline/graphql';
import { z } from 'zod';

import type { MentionUrlDocument, MentionUrlWorkflow } from '../types/mention-urls';

export const NodeIdSchema = z.number().int().positive();
export type NodeId = z.infer<typeof NodeIdSchema>;

export const ArtifactRelevanceSchema = z.string().min(1).max(500);
export const DocumentMentionUrlSchema = z.custom<MentionUrlDocument>(
  value => typeof value === 'string' && /^mention:document\/.+/.test(value)
);

export const DocumentSelectionArtifactSchema = z.object({
  documents: z.array(
    z.object({
      documentId: z.uuid(),
      documentMentionUrl: DocumentMentionUrlSchema,
      documentVariant: z.enum(GQL.DocumentVariant),
      relevance: ArtifactRelevanceSchema,
      title: z.string(),
    })
  ),
  kind: z.literal('documentSelection'),
});
export type DocumentSelectionArtifact = z.infer<typeof DocumentSelectionArtifactSchema>;

const WriterDocumentArtifactDocumentSchema = z.object({
  documentId: z.uuid(),
  documentMentionUrl: DocumentMentionUrlSchema,
  documentVariant: z.enum(GQL.DocumentVariant),
  title: z.string(),
});

export const DraftDocumentArtifactSchema = z.object({
  documents: z.array(WriterDocumentArtifactDocumentSchema),
  kind: z.literal('draftDocument'),
});
export type DraftDocumentArtifact = z.infer<typeof DraftDocumentArtifactSchema>;

export const EditedDocumentArtifactSchema = z.object({
  documents: z.array(WriterDocumentArtifactDocumentSchema),
  kind: z.literal('editedDocument'),
});
export type EditedDocumentArtifact = z.infer<typeof EditedDocumentArtifactSchema>;

/*
 * The report subagent records this artifact once the user has approved its
 * plan and the report row exists. `reportId` is the row the `createReport`
 * tool created, so the closing answer and the artifact bar name the same
 * report.
 */
export const CreatedReportArtifactSchema = z.object({
  kind: z.literal('createdReport'),
  name: z.string(),
  reportId: z.uuid(),
});
export type CreatedReportArtifact = z.infer<typeof CreatedReportArtifactSchema>;

/**
 * The skill subagent records this artifact once the user has approved a proposed skill and
 * it is saved, as a new skill (`created`) or to an existing skill's draft (`updated`).
 */
export const SavedSkillArtifactSchema = z.object({
  action: z.enum(['created', 'updated']),
  kind: z.literal('savedSkill'),
  name: z.string(),
  skillId: z.uuid(),
});
export type SavedSkillArtifact = z.infer<typeof SavedSkillArtifactSchema>;

export const WorkflowMentionUrlSchema = z.custom<MentionUrlWorkflow>(
  value => typeof value === 'string' && /^mention:workflow\/.+/.test(value)
);
export const CreatedWorkflowArtifactSchema = z.object({
  kind: z.literal('createdWorkflow'),
  status: z.union([z.literal('created_draft'), z.literal('launched')]),
  title: z.string(),
  workflowId: z.uuid(),
  workflowMentionUrl: WorkflowMentionUrlSchema,
});
export type CreatedWorkflowArtifact = z.infer<typeof CreatedWorkflowArtifactSchema>;

export const NodeArtifactSchema = z.discriminatedUnion('kind', [
  CreatedReportArtifactSchema,
  CreatedWorkflowArtifactSchema,
  DocumentSelectionArtifactSchema,
  DraftDocumentArtifactSchema,
  EditedDocumentArtifactSchema,
  SavedSkillArtifactSchema,
]);
export type NodeArtifact = z.infer<typeof NodeArtifactSchema>;

/**
 * Lifecycle status of a persisted NodeOutput. `streaming` means a
 * mid-stream throttle snapshot — every executeNodeActivity flips its
 * outputs to `completed` on terminal write. Distinct vocabulary from
 * the per-node lifecycle state persisted on `PlanState.nodeStates`, and
 * from `PlanStateStatus` (`planning | executing | done | failed`),
 * which models the whole turn. These vocabularies are not unified
 * because they describe different cardinalities — node output vs node
 * lifecycle vs turn lifecycle.
 */
export const NODE_OUTPUT_STATUSES = {
  completed: 'completed',
  streaming: 'streaming',
} as const;
export const NodeOutputStatusSchema = z.enum(NODE_OUTPUT_STATUSES);
export type NodeOutputStatus = z.infer<typeof NodeOutputStatusSchema>;

export const NodeOutputSchema = z.object({
  artifacts: z.array(NodeArtifactSchema).optional(),
  /**
   * The canonical user-visible output for this node. This is the stream
   * that landed in `nodeChunks`: LLM text deltas plus json-tool-call fences
   * emitted via `chatClient.onSendToolCall`. Downstream agents read this
   * same field via priorOutputs.
   */
  displayText: z.string(),
  /**
   * Answer signal: did this node produce something the user actually sees —
   * prose (the LLM's `text-delta` output, tool-call/reasoning fences excluded)
   * or a user-facing artifact (a document/report card)? This is a boolean, not
   * text: nothing renders it (the client renders `displayText`; the finalize
   * backstop renders the `documentSelection` artifact), so persisting the full
   * prose again would duplicate it in JSONB for no reader. Computed at persist
   * time from the runner's returned prose and its artifacts — see
   * `getHasVisibleAnswer`.
   *
   * Optional because it is a completion-time field: mid-stream `streaming`
   * snapshots and pre-existing persisted outputs omit it. `undefined` therefore
   * means "indeterminate" (legacy row / snapshot), which `hasUserFacingAnswer`
   * treats differently from a present `false`. A node that only ran tools (the
   * AI-47 blank-turn shape) persists `false`, which is what lets finalize tell
   * a real answer apart from a tool-only handoff.
   */
  hasVisibleAnswer: z.boolean().optional(),
  nodeId: NodeIdSchema,
  /**
   * `streaming` marks a mid-stream snapshot persisted by
   * `executeNodeActivity`'s throttle (every 500ms while the node runs).
   * Refresh-resilient clients use it to tell "in-flight, more deltas
   * coming" apart from "the node is done." Activity failures still finish
   * as `completed` outputs; the user-facing error line lives in
   * `displayText`.
   */
  status: NodeOutputStatusSchema,
  tokensUsed: z.number().int().nonnegative(),
  /** Marker set when `displayText` was truncated by the persistence layer. */
  truncated: z.boolean().optional(),
  type: z.literal('text'),
});
export type NodeOutput = z.infer<typeof NodeOutputSchema>;

/**
 * Lightweight reference to a completed-or-failed plan node, kept in
 * workflow memory in place of the full `NodeOutput`. The full output
 * lives in `planState.nodeOutputs` in the DB; activity replays read
 * from there, and the workflow only needs enough to evaluate readiness
 * and aggregate token usage. Keeping this ref small is what prevents
 * Temporal history events from blowing past the 25KB soft limit when
 * researcher / summarizer nodes return long markdown bodies.
 */
/**
 * Terminal node-level status as seen by the workflow. Distinct from
 * `NodeOutputStatus` above: NodeRef tracks "did this node succeed or
 * fail" for scheduling decisions, while NodeOutput tracks "is this
 * persisted snapshot a mid-stream chunk or the final write."
 */
export const NODE_REF_STATUSES = {
  completed: 'completed',
  failed: 'failed',
} as const;
export const NodeRefStatusSchema = z.enum(NODE_REF_STATUSES);
export type NodeRefStatus = z.infer<typeof NodeRefStatusSchema>;

export const NodeRefSchema = z.object({
  nodeId: NodeIdSchema,
  status: NodeRefStatusSchema,
  tokensUsed: z.number().int().nonnegative(),
});
export type NodeRef = z.infer<typeof NodeRefSchema>;
